import { ApiError } from '../lib/api.js';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';

import { holdsEveryJob, type ApprovalRequest, type Member, type ModelCallTrace } from '@ai-studio/shared';
import { api } from '../lib/api.js';
import { splitContext, withContext, type ContextChip } from '../lib/ai-context.js';
import {
  EFFORT_NOTE,
  EFFORT_OPTIONS,
  ULTRACODE_NOTE,
  effortSupport,
  effortValue,
  parseEffort,
  ultracodeSupport,
} from '../lib/member-modes.js';
import { AttachmentTray, MediaStrip, splitMedia, useAttachments } from './ChatMedia.js';
import { SubagentPanel } from './SubagentPanel.js';
import { Icon, type IconName } from './ui/icons.js';

import { CHAT_VISIBLE_LIMIT, useAgents } from '../state/agents.js';
import type { ActivityEntry } from '../state/agents.js';
import { useGeneration } from '../state/generation.js';
import { useTeam } from '../state/team.js';
import { useWorkspace } from '../state/workspace.js';
import { useFollow } from '../state/follow.js';
import { WEBSITE } from '../lib/edition.js';
import { useDevDetails } from '../lib/dev-details.js';
import { prismaFeed, prismaStatus } from '../lib/prisma-status.js';
import { PrismaOrb } from './prisma/PrismaOrb.js';
import { SuperPrisma } from './prisma/SuperPrisma.js';

/**
 * One member's conversation.
 *
 * The Assistant is open by default. The name tags along the bottom open each
 * worker's own chat, and the composer talks to whoever is open. Each chat
 * shows its newest lines; the rest stay stored with the project.
 */
const CHIP_ICONS: Record<ContextChip['kind'], IconName> = {
  scene: 'scene',
  entity: 'cube',
  model: 'model',
  part: 'layers',
  file: 'file',
  play: 'play',
  sound: 'audio',
  analysis: 'data',
};

/** localStorage key of the chat's auto-scroll checkbox ('0' = off). */
const AUTO_SCROLL_KEY = 'prism.chat.autoScroll';

export interface ChatProps {
  /** Editor context attached to outgoing messages (shown as chips). */
  chips?: readonly ContextChip[];
  /** Whether the chips are attached; the user can switch them off. */
  attachContext?: boolean;
  onToggleContext?: () => void;
  /** Compact layout for the right-hand dock. */
  dock?: boolean;
}

export function Chat({ chips = [], attachContext = true, onToggleContext, dock = false }: ChatProps = {}) {
  const projectId = useWorkspace((s) => s.projectId);
  const members = useTeam((s) => s.members);
  const activity = useAgents((s) => s.activity);
  const activeChatMemberId = useAgents((s) => s.activeChatMemberId);
  const setActiveChat = useAgents((s) => s.setActiveChat);
  const runtime = useAgents((s) => s.runtime);
  const approvals = useAgents((s) => s.approvals);
  const send = useAgents((s) => s.sendToMember);
  const stopMember = useAgents((s) => s.stopMember);
  const decide = useAgents((s) => s.decide);

  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [showApprovals, setShowApprovals] = useState(true);
  const [sendError, setSendError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const attachments = useAttachments(projectId);
  const logRef = useRef<HTMLDivElement | null>(null);

  const assistant = members.find((m) => m.role === 'assistant');
  // Only Prisma: the chat is always hers.
  const active = assistant ?? null;
  const running = active ? runtime[active.id]?.state === 'running' : false;

  // The operator's technical view, or (everyone else) Prisma's streamlined one.
  const dev = useDevDetails();
  const chatEntries = useMemo(() => (active ? activity.filter((entry) => entry.chatMemberId === active.id) : []), [activity, active]);
  const visible = useMemo(() => (dev ? chatEntries.slice(-CHAT_VISIBLE_LIMIT) : []), [chatEntries, dev]);
  // Everyone else: their messages, her replies and pictures, and her work in
  // between as steps in her words. No tool names, thinking, notes or errors.
  const feed = useMemo(
    () => (dev || !active ? [] : prismaFeed(chatEntries, active.id, (text) => splitMedia(text).images.length > 0).slice(-CHAT_VISIBLE_LIMIT)),
    [chatEntries, dev, active],
  );
  const status = active ? prismaStatus(chatEntries, active.id, running) : { mood: 'idle' as const, line: '' };

  // Auto-scroll: keep the newest line in view while it is on. Follows every
  // change of the log (streamed text growing a line, new lines once the chat
  // is at its visible limit, pictures finishing loading), not just the line
  // count, which stopped changing at the limit and froze the old scroll.
  const [autoScroll, setAutoScroll] = useState(() => {
    try {
      return localStorage.getItem(AUTO_SCROLL_KEY) !== '0';
    } catch {
      return true;
    }
  });
  useEffect(() => {
    const log = logRef.current;
    if (!autoScroll || !log) return;
    const bottom = () => {
      log.scrollTop = log.scrollHeight;
    };
    bottom();
    const observer = new MutationObserver(bottom);
    observer.observe(log, { childList: true, subtree: true, characterData: true });
    log.addEventListener('load', bottom, true);
    return () => {
      observer.disconnect();
      log.removeEventListener('load', bottom, true);
    };
  }, [autoScroll, active?.id, projectId]);

  if (!projectId) return <p className="muted explorer-hint">No project open.</p>;

  const canSend = (text.trim() !== '' || attachments.tags !== '') && !attachments.uploading;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!projectId || !active || !canSend || busy) return;

    const typed = text.trim();
    const words = typed || 'See the attached files.';
    const message = attachments.tags ? `${words}\n${attachments.tags}` : words;
    const outgoing = attachContext ? withContext(message, chips) : message;
    setBusy(true);
    setText('');
    setSendError(null);
    try {
      await send(projectId, active.id, outgoing);
      attachments.clear();
    } catch (error) {
      // Put the message back and say why. Swallowing this is what made a failed
      // send look like nothing had happened at all. (Prisma's view: no technical reason.)
      setText(typed);
      setSendError(
        dev
          ? error instanceof Error
            ? error.message
            : 'Could not send that message.'
          : error instanceof ApiError && error.status === 401
            ? 'Sign in to your Prism Engine Cloud account to talk to Prisma.'
            : "Prisma didn't catch that one. Send it again in a moment.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`chat${dock ? ' chat-dock' : ''}${dev ? '' : ' chat-prisma'}`}>
      {!dev && (
        <header className="prisma-head">
          <PrismaOrb mood={status.mood} size={dock ? 44 : 52} />
          <div className="prisma-id">
            <strong className="prisma-name">{active?.name ?? 'Prisma'}</strong>
            <span className={`prisma-status is-${status.mood}`} aria-live="polite">
              {status.line}
              {running && <span className="prisma-dots" aria-hidden="true" />}
            </span>
          </div>
          <SuperPrisma />
        </header>
      )}
      {dev && <header className="chat-head">
        <span className="chat-avatar" aria-hidden="true">
          <Icon name="sparkles" size={14} />
        </span>
        <strong>{active?.name ?? 'Prisma'}</strong>
        <span className="muted chat-model">
          {WEBSITE
            ? 'pick the model below'
            : active?.bindings[0]
              ? `${active.bindings[0].provider}${active.bindings[0].model ? ` / ${active.bindings[0].model}` : ''}`
              : 'no model configured'}
        </span>
        <label className="chat-autoscroll" title="Keep the newest message in view">
          <input
            type="checkbox"
            checked={autoScroll}
            onChange={(event) => {
              const on = event.target.checked;
              setAutoScroll(on);
              try {
                localStorage.setItem(AUTO_SCROLL_KEY, on ? '1' : '0');
              } catch {
                // Not remembered (private window); still applies now.
              }
            }}
          />
          Auto-scroll
        </label>
      </header>}

      {approvals.length > 0 && showApprovals && (
        <section className="approvals" aria-live="polite">
          <h4>{approvals.length} waiting on you</h4>
          {approvals.map((approval) => (
            <ApprovalCard key={approval.id} approval={approval} onDecide={decide} />
          ))}
        </section>
      )}

      <div className="chat-log" aria-live="polite" ref={logRef}>
        {(dev ? visible.length : feed.length) === 0 && (
          <p className="muted chat-empty">
            {!dev
              ? "Hi! I'm Prisma. Tell me what you'd like to make, and we'll build it together."
              : active && active.role !== 'assistant'
                ? `Nothing yet. Send a message to ${active.name}.`
                : 'Nothing yet. Ask Prisma to do something.'}
          </p>
        )}

        {!dev &&
          feed.map((item, index) => {
            if (item.kind === 'steps')
              return <PrismaSteps key={`steps-${item.id}`} steps={item.steps} live={running && index === feed.length - 1} />;
            const entry = item.entry;
            const mine = entry.memberId === 0;
            return (
              <article key={entry.id} className={`prisma-bubble ${mine ? 'is-you' : 'is-prisma'}`}>
                {entry.kind === 'tool' ? (
                  <MediaStrip projectId={projectId} {...splitMedia(entry.text)} />
                ) : (
                  <BubbleText text={mine ? entry.text.replace(/^You: /, '') : entry.text} projectId={projectId} />
                )}
              </article>
            );
          })}

        {dev && visible.map((entry) => {
          const member = members.find((m) => m.id === entry.memberId);
          const isUser = entry.memberId === 0;
          const label = isUser ? 'You' : (member?.name ?? `member #${entry.memberId}`);
          const state = runtime[entry.memberId]?.state;

          return (
            <article key={entry.id} className={`bubble bubble-${entry.kind}`}>
              <details className="call-inspector">
                <summary aria-label={`Inspect ${label} ${entry.kind}`}>
                  <header>
                    <span className="bubble-who">{label}</span>
                    {state === 'waiting_approval' && (
                      <span className="bubble-state warn">waiting</span>
                    )}
                  </header>

                  {entry.kind === 'tool' && <ToolLine text={entry.text} bad={entry.ok === false} projectId={projectId} />}

                  {entry.kind !== 'tool' && <BubbleText text={entry.text} projectId={projectId} />}
                  <span className="muted call-hint">Click to inspect context and tokens</span>
                </summary>
                <CallDetails entry={entry} projectId={projectId} />
              </details>
              {entry.kind === 'tool' && <SubagentPanel entry={entry} />}
            </article>
          );
        })}
      </div>

      {sendError && (
        <p className="chat-error" role="alert">
          {sendError}
        </p>
      )}

      {attachments.error && (
        <p className="chat-error" role="alert">
          {attachments.error}
        </p>
      )}

      <form
        className={`chat-input${dragging ? ' is-dropping' : ''}`}
        onSubmit={submit}
        onDragOver={(event) => {
          if (!event.dataTransfer.types.includes('Files')) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
          setDragging(true);
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
        }}
        onDrop={(event) => {
          if (!event.dataTransfer.files.length) return;
          event.preventDefault();
          setDragging(false);
          attachments.add([...event.dataTransfer.files]);
        }}
      >
        <AttachmentTray items={attachments.items} onRemove={attachments.remove} />
        {chips.length > 0 && (
          <div className={`ctx-chips${attachContext ? '' : ' is-off'}`}>
            {chips.map((chip) => (
              <span key={`${chip.kind}:${chip.value}`} className={`ctx-chip ctx-${chip.kind}`} title={chip.value}>
                <Icon name={CHIP_ICONS[chip.kind]} size={12} />
                {chip.label}
              </span>
            ))}
            {onToggleContext && (
              <button
                type="button"
                className="ctx-toggle"
                onClick={onToggleContext}
                title={attachContext ? 'Do not attach this context' : 'Attach this context'}
              >
                {attachContext ? <Icon name="x" size={11} /> : 'Attach'}
              </button>
            )}
          </div>
        )}
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onPaste={(e) => {
            // Pasted pictures (screenshots) and copied files attach; pasted text stays text.
            const files = [...e.clipboardData.files];
            if (!files.length) return;
            e.preventDefault();
            attachments.add(files);
          }}
          onKeyDown={(e) => {
            // Enter sends; Shift+Enter is a newline.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void submit(e as unknown as FormEvent);
            }
          }}
          placeholder={
            !dev
              ? active?.role === 'assistant' || !active
                ? 'Tell Prisma what to make… (drop in pictures or notes too)'
                : `Message ${active.name}…`
              : active?.bindings.length || WEBSITE
              ? active?.role === 'assistant'
                ? 'Ask Prisma, or name a task like "work on fix-login-redirect". Paste or drop pictures and text files here.'
                : `Message ${active?.name ?? ''} (paste or drop pictures and text files)`
              : 'Sign in to your Prism Engine Cloud account to talk to Prisma'
          }
          rows={2}
          disabled={busy || !active}
        />
        {dock && <FollowToggle />}
        {active && dev && (
          <div className="chat-modes" role="group" aria-label={`${active.name} run modes`}>
            <EffortSelect projectId={projectId} member={active} onError={setSendError} />
            <UltracodeButton projectId={projectId} member={active} onError={setSendError} />
          </div>
        )}
        {active && active.role === 'assistant' && !WEBSITE && (
          <>
            <AutonomousButton projectId={projectId} assistant={active} onError={setSendError} />
            <AutoApproveButton projectId={projectId} assistant={active} onError={setSendError} />
          </>
        )}
        <button
          className="primary chat-send"
          type="submit"
          disabled={busy || !canSend || !active}
          title={attachments.uploading ? 'Attaching…' : 'Send (Enter)'}
        >
          <Icon name="send" size={14} />
          <span>Send</span>
        </button>
        {running && active && (
          <button
            className="chat-end"
            type="button"
            onClick={() => {
              setSendError(null);
              void stopMember(active.id).catch((error) => {
                setSendError(dev && error instanceof Error ? error.message : "Prisma couldn't stop just yet. Try again in a moment.");
              });
            }}
          >
            {dev ? 'End' : 'Stop'}
          </button>
        )}
      </form>

      {approvals.length > 0 && (
        <button className="link-btn" onClick={() => setShowApprovals((v) => !v)}>
          {showApprovals ? 'Hide approvals' : `Show ${approvals.length} approvals`}
        </button>
      )}
    </div>
  );
}

/** Follow assistant on/off (the eye), inside the chat box. */
function FollowToggle() {
  const following = useFollow((s) => s.following);
  const setFollowing = useFollow((s) => s.setFollowing);
  const last = useFollow((s) => s.last);
  return (
    <button
      type="button"
      className={`chat-follow${following ? ' is-on' : ''}`}
      aria-pressed={following}
      onClick={() => setFollowing(!following)}
      title={
        following
          ? `Follow assistant: the editor shows whatever the assistant works on in this project (only while this tab is open)${last ? ` — last: ${last}` : ''}. Click to stop following.`
          : 'Follow assistant is off: the editor stays where you are. Click to follow the assistant.'
      }
    >
      <Icon name={following ? 'eye' : 'eyeOff'} size={14} />
    </button>
  );
}

/** Reports a failed save of a chat-bar setting; null clears the message. */
type SaveError = (message: string | null) => void;

/**
 * Save one member setting from the chat bar. The controls show the saved
 * value, so a failed save snaps back; the reason goes to `onError` (the chat's
 * error line) rather than only to the Team panel.
 */
export async function saveMember(
  projectId: number,
  memberId: number,
  patch: Parameters<ReturnType<typeof useTeam.getState>['patch']>[2],
  what: string,
  onError: SaveError | undefined,
): Promise<void> {
  onError?.(null);
  const saved = await useTeam.getState().patch(projectId, memberId, patch);
  if (!saved) onError?.(`Could not save ${what}: ${useTeam.getState().error ?? 'the server did not accept it.'}`);
}

/**
 * Start / Stop autonomous mode: toggles autonomousWork on the project's
 * Assistant (the same flag the Team page used to show as a checkbox).
 */
export function AutonomousButton({
  projectId,
  assistant,
  onError,
}: {
  projectId: number;
  assistant: Member;
  onError?: SaveError;
}) {
  const [saving, setSaving] = useState(false);
  const capable = holdsEveryJob(assistant.jobs);
  const on = assistant.autonomousWork === true && capable;
  const title = !capable
    ? 'Autonomous mode is not available for this project.'
    : on
      ? "Prisma is working on her own: she works through Up next on the board and plans the next steps when it is empty. Click to stop."
      : "Let Prisma work on her own: she works through Up next on the board, plans new steps when it is empty, and doesn't stop to ask.";
  return (
    <button
      type="button"
      className={`chat-auto${on ? ' is-running' : ''}`}
      title={title}
      aria-pressed={on}
      disabled={!capable || saving}
      onClick={async () => {
        setSaving(true);
        try {
          await saveMember(projectId, assistant.id, { autonomousWork: !on }, 'autonomous mode', onError);
        } finally {
          setSaving(false);
        }
      }}
    >
      <span className="chat-auto-dot" aria-hidden="true" />
      {on ? 'Stop autonomous mode' : 'Start autonomous mode'}
    </button>
  );
}

/**
 * Auto-Approve on / off: every approval Prisma asks for (shell commands, file
 * edits, deletes, moves, external tools) goes ahead at once, and so does every
 * generation request and result (the project's auto-approve-everything setting).
 * Turning it on also releases whatever is already waiting.
 */
export function AutoApproveButton({
  projectId,
  assistant,
  onError,
}: {
  projectId: number;
  assistant: Member;
  onError?: SaveError;
}) {
  const [saving, setSaving] = useState(false);
  const on = assistant.autoApproveAll === true;
  const title = on
    ? 'Auto-Approve is on: everything Prisma asks to do (commands, file changes, generations) is approved at once. Click to ask again.'
    : 'Auto-approve everything: commands, file changes, deletes, moves and generations go ahead without asking you.';
  return (
    <button
      type="button"
      className={`chat-auto chat-auto-approve${on ? ' is-running' : ''}`}
      title={title}
      aria-pressed={on}
      disabled={saving}
      onClick={async () => {
        setSaving(true);
        try {
          await saveMember(projectId, assistant.id, { autoApproveAll: !on }, 'auto-approve', onError);
          const gen = useGeneration.getState();
          if (gen.projectId === projectId && gen.settings.autoApprove !== !on) await gen.setAutoApprove(!on);
        } finally {
          setSaving(false);
        }
      }}
    >
      <span className="chat-auto-dot" aria-hidden="true" />
      {on ? 'Stop auto-approve' : 'Auto-Approve'}
    </button>
  );
}

/**
 * Effort for one member's model calls (Anthropic API and Claude CLI only).
 * The worker reads it per call, so a change applies without a restart.
 */
export function EffortSelect({
  projectId,
  member,
  onError,
}: {
  projectId: number;
  member: Member;
  onError?: SaveError;
}) {
  const [saving, setSaving] = useState(false);
  const support = effortSupport(member);
  // Shows what applies: an unsupported provider runs at its own default.
  const current = support.ok ? (member.effort ?? null) : null;
  const label = EFFORT_OPTIONS.find((option) => option.value === current)?.label ?? 'Default';
  const title = support.ok
    ? `Effort: ${label}. ${EFFORT_NOTE} Applies from the next model call.`
    : (support.reason ?? '');
  return (
    <label className={`chat-effort${current ? ' is-set' : ''}`} title={title}>
      <Icon name="gauge" size={13} />
      <select
        aria-label="Effort"
        value={effortValue(current)}
        disabled={!support.ok || saving}
        onChange={async (event) => {
          const effort = parseEffort(event.target.value);
          setSaving(true);
          try {
            await saveMember(projectId, member.id, { effort }, 'the effort', onError);
          } finally {
            setSaving(false);
          }
        }}
      >
        {EFFORT_OPTIONS.map((option) => (
          <option key={option.value ?? 'default'} value={effortValue(option.value)}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Ultracode on/off: lets the member start parallel sub-agents (spawn_agents on
 * the API loop, workflows on the Claude CLI). Toggling restarts its worker.
 */
export function UltracodeButton({
  projectId,
  member,
  onError,
}: {
  projectId: number;
  member: Member;
  onError?: SaveError;
}) {
  const [saving, setSaving] = useState(false);
  const support = ultracodeSupport(member);
  const on = member.ultracode === true && support.ok;
  const title = !support.ok
    ? (support.reason ?? '')
    : on
      ? `Ultracode is on. ${ULTRACODE_NOTE} Click to turn it off.`
      : `Turn on Ultracode. ${ULTRACODE_NOTE}`;
  return (
    <button
      type="button"
      className={`chat-ultra${on ? ' is-on' : ''}`}
      title={title}
      aria-pressed={on}
      disabled={!support.ok || saving}
      onClick={async () => {
        setSaving(true);
        try {
          await saveMember(projectId, member.id, { ultracode: !on }, 'Ultracode', onError);
        } finally {
          setSaving(false);
        }
      }}
    >
      <Icon name="layers" size={13} />
      <span>Ultracode</span>
    </button>
  );
}

/** Message text, with an attached editor-context line shown as a tag and pictures / documents shown inline. */
function BubbleText({ text, projectId }: { text: string; projectId: number }) {
  const { context, body } = splitContext(text);
  const media = splitMedia(body);
  return (
    <>
      {context && <span className="bubble-context">{context}</span>}
      {media.body && <p className="bubble-text">{media.body}</p>}
      <MediaStrip projectId={projectId} images={media.images} files={media.files} />
    </>
  );
}

/** A tool's chat line, with the screenshots / renders it returned below it. */
function ToolLine({ text, bad, projectId }: { text: string; bad: boolean; projectId: number }) {
  const media = splitMedia(text);
  return (
    <>
      <p className={`bubble-tool${bad ? ' is-bad' : ''}`}>
        <span aria-hidden="true">⚙</span> {media.body}
      </p>
      <MediaStrip projectId={projectId} images={media.images} files={media.files} />
    </>
  );
}

/** Steps shown while a run of work is live; earlier ones fold into a count. */
const LIVE_STEPS = 5;

/**
 * A run of Prisma's work between two messages, step by step in her words.
 * Open while she is on it (the newest steps, the current one pulsing); once
 * she moves on it folds into one line the person can open again.
 */
function PrismaSteps({ steps, live }: { steps: readonly string[]; live: boolean }) {
  const [open, setOpen] = useState(live);
  useEffect(() => setOpen(live), [live]);
  const hidden = live ? Math.max(0, steps.length - LIVE_STEPS) : 0;
  const shown = steps.slice(hidden);
  const count = `${steps.length} step${steps.length === 1 ? '' : 's'}`;
  return (
    <div className={`prisma-steps${live ? ' is-live' : ''}`}>
      <button type="button" className="prisma-steps-head" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <Icon name={live ? 'sparkles' : 'check'} size={12} />
        <span>{live ? 'On it' : 'What I did'}</span>
        <span className="prisma-steps-count">{count}</span>
      </button>
      {open && (
        <ol className="prisma-steps-list">
          {hidden > 0 && <li className="is-earlier">…and {hidden} earlier</li>}
          {shown.map((step, i) => (
            <li key={hidden + i} className={live && i === shown.length - 1 ? 'is-now' : 'is-done'}>
              {step}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function CallDetails({ entry, projectId }: { entry: ActivityEntry; projectId: number }) {
  const call = useAgents((state) => (entry.callId ? state.calls[entry.callId] : undefined));
  const [trace, setTrace] = useState<ModelCallTrace | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const details = container.current?.closest('details');
    let disposed = false;
    async function load() {
      if (!details?.open || !entry.callId) return;
      setLoading(true);
      try {
        const response = await api.get<{ trace: ModelCallTrace }>(
          `/agents/projects/${projectId}/calls/${encodeURIComponent(entry.callId)}`,
        );
        if (!disposed) {
          setTrace(response.trace);
          setError(null);
        }
      } catch (error) {
        if (!disposed)
          setError(error instanceof Error ? error.message : 'Could not load call details.');
      } finally {
        if (!disposed) setLoading(false);
      }
    }
    const toggle = () => {
      if (details?.open) void load();
    };
    details?.addEventListener('toggle', toggle);
    void load();
    return () => {
      disposed = true;
      details?.removeEventListener('toggle', toggle);
    };
  }, [entry.callId, entry.detail, entry.ok, call?.status, projectId]);
  const tool = trace?.toolResults.find((result) => result.id === entry.toolCallId);
  const tokens = (value: number | null | undefined) =>
    value == null ? 'Not reported' : value.toLocaleString();
  return (
    <div ref={container} className="call-details">
      {!entry.callId && (
        <p className="muted">
          No model-call metadata was recorded for this message. User/status messages do not have
          independent token usage; older messages cannot be reconstructed.
        </p>
      )}
      {entry.memberId === 0 && (
        <p className="muted">
          Token counts below belong to the model call that received this message, not to a separate
          user-message call.
        </p>
      )}
      {entry.kind === 'tool' && (
        <p className="muted">
          These tokens belong to the model round that requested this tool. Sibling tools share the
          same counts; tool execution itself uses no model tokens.
        </p>
      )}
      {entry.callId && (
        <>
          <p>
            <strong>
              {call?.provider ?? trace?.provider} / {call?.model ?? trace?.model}
            </strong>{' '}
            · Key slot {call?.keySlot ?? trace?.keySlot} ·{' '}
            {call?.status ?? trace?.status ?? 'Loading'}
          </p>
          <p>
            Input tokens: <strong>{tokens(call?.inputTokens ?? trace?.inputTokens)}</strong> ·
            Output tokens: <strong>{tokens(call?.outputTokens ?? trace?.outputTokens)}</strong> ·
            Effective local context:{' '}
            <strong>
              {(call?.contextChars ?? trace?.contextChars)?.toLocaleString() ?? 'Unknown'}{' '}
              characters
            </strong>
          </p>
          {loading && <p className="muted">Loading call context…</p>}
          {error && <p role="alert">{error}</p>}
        </>
      )}
      {entry.kind === 'tool' && (
        <>
          <h4>Tool arguments</h4>
          <pre>{JSON.stringify(tool?.arguments ?? entry.arguments ?? {}, null, 2)}</pre>
          <h4>Tool output</h4>
          <pre>
            {tool?.output ??
              entry.detail ??
              (entry.ok === null ? 'Waiting for approval or execution.' : entry.text)}
          </pre>
        </>
      )}
      {trace && (
        <>
          <p className="muted">
            Duration: {(trace.durationMs / 1000).toFixed(2)}s. Context is the effective local
            replay. Stateful providers may also use remote history; reported input tokens include
            provider-side context. No hidden provider thoughts or context are available here.
          </p>
          {trace.error && (
            <>
              <h4>Call error</h4>
              <pre>{trace.error}</pre>
            </>
          )}
          <details>
            <summary>Context sent to the model</summary>
            <pre>{JSON.stringify(trace.context, null, 2)}</pre>
          </details>
          <details>
            <summary>Available tool schemas</summary>
            <pre>{JSON.stringify(trace.tools, null, 2)}</pre>
          </details>
          <details>
            <summary>Actual provider request bodies (no auth headers)</summary>
            <pre>
              {trace.requests.length
                ? JSON.stringify(trace.requests, null, 2)
                : 'This transport did not record request bodies.'}
            </pre>
          </details>
          <details>
            <summary>Model output</summary>
            <pre>{trace.output || '(No text; see tool calls)'}</pre>
          </details>
        </>
      )}
      {!trace && entry.kind !== 'tool' && <pre>{entry.text}</pre>}
    </div>
  );
}

/** One pending decision, with the diff or command that prompted it. */
function ApprovalCard({
  approval,
  onDecide,
}: {
  approval: ApprovalRequest;
  onDecide: (id: string, decision: 'approved' | 'denied') => Promise<void>;
}) {
  const [showDiff, setShowDiff] = useState(false);
  const [busy, setBusy] = useState(false);

  async function decide(decision: 'approved' | 'denied') {
    setBusy(true);
    try {
      await onDecide(approval.id, decision);
    } finally {
      setBusy(false);
    }
  }

  const detail = approval.diff || (approval.command ? `$ ${approval.command.join(' ')}` : null);

  return (
    <div className={`approval risk-${approval.risk}`}>
      <header>
        <strong>{approval.memberName}</strong>
        <span className="approval-tool">{approval.tool}</span>
        <span className={`risk-chip risk-${approval.risk}`}>{approval.risk}</span>
      </header>

      <p className="approval-reason">{approval.reason}</p>
      {approval.targetPath && <code className="approval-path">{approval.targetPath}</code>}

      {detail && (
        <>
          <button className="link-btn" onClick={() => setShowDiff((v) => !v)}>
            {showDiff ? 'Hide' : 'Review'}
          </button>
          {showDiff && <pre className="diff">{detail}</pre>}
        </>
      )}

      <div className="row-actions">
        <button className="ghost" onClick={() => void decide('denied')} disabled={busy}>
          Deny
        </button>
        <button className="primary" onClick={() => void decide('approved')} disabled={busy}>
          Approve
        </button>
      </div>
    </div>
  );
}
